import { create } from 'zustand';

/**
 * Per-dispatch guard for the Dispatch log's AUTOMATIC Clay re-check
 * (`components/play-flow/dispatch-log/useSendRecheck.ts`; PR #1715 review,
 * finding 6). Same shape as `member-sync-store.ts`.
 *
 * Why state, not a ref: the send card must fire the re-check once per REAL
 * mount. A cleanup-only ref is the classic StrictMode casualty, and a
 * reset-on-mount ref fires twice under the dev setup→cleanup→setup
 * double-invoke (docs/v4-system/strictmode-resilient-effects.md). Keyed on
 * module-scope state instead:
 *
 *   - no entry, or `'checked'` → the claim WINS (a first open, or a real
 *     remount after the last check settled — "Terra checks again each time
 *     you open the play" stays literally true);
 *   - `'checking'` → the claim JOINS the one in flight (StrictMode's second
 *     setup, or a second card for the same send) and fires nothing.
 *
 * The server is single-flight per dispatch as well, so a stray extra call
 * would be harmless — this keeps it from happening at all.
 */
export type SendRecheckEntry = 'checking' | 'checked';

export interface SendRecheckState {
  byDispatch: Record<string, SendRecheckEntry>;
}

/** Exported per the store convention — reset idiom is `useSendRecheckStore.setState(initialState, true)`. */
export const initialState: SendRecheckState = { byDispatch: {} };

export const useSendRecheckStore = create<SendRecheckState>(() => ({ ...initialState }));

/** `true` → the caller fires the re-check; `false` → one is already in flight, join it. */
export function claimAutoRecheck(dispatchId: string): boolean {
  if (useSendRecheckStore.getState().byDispatch[dispatchId] === 'checking') return false;
  useSendRecheckStore.setState((s) => ({ byDispatch: { ...s.byDispatch, [dispatchId]: 'checking' } }));
  return true;
}

/** The re-check settled (either way) — the next real mount may claim again. */
export function settleAutoRecheck(dispatchId: string): void {
  useSendRecheckStore.setState((s) => ({ byDispatch: { ...s.byDispatch, [dispatchId]: 'checked' } }));
}
